import { cn } from '@goaltree/ui/lib/utils'
import { Check, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { chosenSplitTitles, useOnboardingStore } from '@/store/onboarding.store'

const card = 'rounded-2xl border border-border/60 bg-card/40 px-4 py-4 backdrop-blur-sm'
const pill =
  'cursor-pointer rounded-full border px-3.5 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50'

export function GoalAnalyzing() {
  const { t } = useTranslation()
  return (
    <p className="flex items-center gap-2 text-xs text-muted-foreground">
      <Loader2 className="size-3.5 animate-spin" />
      {t('planner.analyzing')}
    </p>
  )
}

// "This looks like N goals": edit/untick the titles, then plan them separately
// or keep one goal (the others become milestones). `room` is how many more
// goals the Free plan allows (Infinity on Pro).
export function GoalSplitCard({ room, onChange }: { room: number; onChange: () => void }) {
  const { t } = useTranslation()
  const analysis = useOnboardingStore((s) => s.analysis)
  const titles = useOnboardingStore((s) => s.splitTitles)
  const off = useOnboardingStore((s) => s.splitOff)
  const choice = useOnboardingStore((s) => s.splitChoice)
  const store = useOnboardingStore()
  const proposed = analysis?.goals ?? []
  if (proposed.length < 2) return null

  const checked = chosenSplitTitles()
  const n = checked.length
  const over = n > room
  const choose = (c: typeof choice) => {
    store.setSplitChoice(c)
    onChange()
  }

  if (choice) {
    return (
      <div className={cn(card, 'flex items-start justify-between gap-3 py-3')}>
        <div className="min-w-0 text-sm">
          <p className="font-medium text-foreground">
            {choice === 'split' ? t('planner.split.chosen', { n }) : t('planner.split.keptOne')}
          </p>
          {choice === 'split' && (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{checked.join(' · ')}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => choose(null)}
          className="shrink-0 cursor-pointer text-xs font-semibold text-primary-text hover:underline"
        >
          {t('planner.split.change')}
        </button>
      </div>
    )
  }

  return (
    <div className={cn(card, 'space-y-3')}>
      <div>
        <p className="text-sm font-semibold text-foreground">
          {t('planner.split.title', { n: proposed.length })}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('planner.split.hint')}</p>
      </div>
      <ul className="space-y-1.5">
        {proposed.map((orig, i) => {
          const on = !off.includes(i)
          return (
            <li key={i} className="flex items-center gap-2">
              <button
                type="button"
                role="checkbox"
                aria-checked={on}
                aria-label={orig}
                onClick={() => {
                  store.setSplitOff(on ? [...off, i] : off.filter((x) => x !== i))
                  onChange()
                }}
                className={cn(
                  'flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-md border transition-colors',
                  on
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-transparent',
                )}
              >
                {on && <Check className="size-3.5" />}
              </button>
              <input
                value={titles[i] ?? orig}
                disabled={!on}
                maxLength={60}
                aria-label={orig}
                onChange={(e) => {
                  const next = proposed.map((p, j) => titles[j] ?? p)
                  next[i] = e.target.value
                  store.setSplitTitles(next)
                  onChange()
                }}
                className="h-9 min-w-0 flex-1 rounded-lg border border-transparent bg-transparent px-2 text-sm text-foreground outline-none transition-colors hover:border-border/60 focus:border-border disabled:text-muted-foreground disabled:line-through"
              />
            </li>
          )
        })}
      </ul>
      {over && (
        <p className="text-xs text-muted-foreground">
          {room > 0 ? t('planner.split.limit', { n: room }) : t('planner.split.limitNone')}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {over && room > 1 ? (
          <button
            type="button"
            onClick={() => {
              const onIdx = proposed.map((_, i) => i).filter((i) => !off.includes(i))
              store.setSplitOff([...off, ...onIdx.slice(room)])
              choose('split')
            }}
            className={cn(pill, 'border-primary/60 bg-primary/10 text-foreground')}
          >
            {t('planner.split.createFirst', { n: room })}
          </button>
        ) : (
          !over && (
            <button
              type="button"
              disabled={n < 2}
              onClick={() => choose('split')}
              className={cn(pill, 'border-primary/60 bg-primary/10 text-foreground')}
            >
              {t('planner.split.create', { n })}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => choose('one')}
          className={cn(
            pill,
            'border-border/60 bg-card/40 text-muted-foreground hover:border-border/80 hover:text-foreground',
          )}
        >
          {t('planner.split.keepOne')}
        </button>
      </div>
    </div>
  )
}

// 1–2 questions about specifics the planner can't know (which projects are
// left, what's in the backlog); answers go into the plan prompt.
export function GoalDetailsCard({ onChange }: { onChange: () => void }) {
  const { t } = useTranslation()
  const questions = useOnboardingStore((s) => s.analysis?.questions ?? [])
  const details = useOnboardingStore((s) => s.details)
  const skipped = useOnboardingStore((s) => s.detailsSkipped)
  const store = useOnboardingStore()
  if (!questions.length || skipped) return null
  return (
    <div className={cn(card, 'space-y-3')}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-foreground">{t('planner.details.title')}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{t('planner.details.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            store.setDetailsSkipped(true)
            onChange()
          }}
          className="shrink-0 cursor-pointer text-xs font-semibold text-muted-foreground hover:text-foreground"
        >
          {t('planner.details.skip')}
        </button>
      </div>
      {questions.map((q) => (
        <label key={q.question} className="block space-y-1.5">
          <span className="block text-sm font-medium text-foreground">{q.question}</span>
          <textarea
            rows={2}
            value={details[q.question] ?? ''}
            maxLength={600}
            placeholder={
              q.hint ? t('planner.details.example', { hint: q.hint }) : t('planner.details.answer')
            }
            onChange={(e) => {
              store.setDetail(q.question, e.target.value)
              onChange()
            }}
            className="w-full resize-none rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground/70 focus:border-primary/60"
          />
        </label>
      ))}
    </div>
  )
}
